import { Grid2 as Grid, Stack, styled, Typography, useTheme } from "@mui/material";
import { unstable_generateUtilityClasses as generateUtilityClasses } from "@mui/utils";
import { UploadDropZone, UploadDropZoneProps } from "@rpldy/upload-drop-zone";
import { MdsUpload } from "@sinch/icons";
import clsx from "clsx";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "../../../MdsIcon/MdsIcon";
import { UploadContainerProps, UploadInputProps } from "../types";

type DropZoneUploadProps = UploadContainerProps;
/**
 * DropZone Image Upload - Drag and drop upload for images(can be used for single or multiple files)
 */

const classes = generateUtilityClasses("SinchDragZoneImageUploader", [
  "uploadStack",
  "uploadIcon",
  "textContainer",
  "hideOnDrag",
  "uploadButton",
  "textContainer",
  "showOnDrag",
  "dragOver",
]);

export const DropZoneImageUploadContainer: React.FC<DropZoneUploadProps> = ({
  value = [],
  endpoint,
  multiple,
  name,
  // oxlint-disable-next-line no-unused-vars
  error, // TODO: implement errors to input
  // oxlint-disable-next-line no-unused-vars
  disabled, // TODO: implement disabled to input
  previewItemComponent: PreviewItem,
  uploadComponent: UploadButtonComponent,
  UploadyProps: uploadyProps,
}) => {
  const indicatorRef = useRef<HTMLDivElement>(null);

  return (
    <StyledUploadDropZone
      destination={{ url: endpoint }}
      enableOnContains
      onDragOverClassName={classes.dragOver}
      params={{
        name: name,
      }}
      shouldHandleDrag
      shouldRemoveDragOver={(e: DragEvent) => !indicatorRef?.current?.contains(e.relatedTarget as Node)}
      sx={{ containerType: "inline-size" }}
      {...uploadyProps}
    >
      <Grid
        ref={indicatorRef}
        className={classes.uploadStack}
        container
        direction="column"
        spacing={1}
        sx={{ padding: { "@200": 3, "@": 1 } }}
      >
        <Grid size={12}>
          <DragZoneImageUploadContent multiple={multiple} uploadButtonComponent={UploadButtonComponent} />
        </Grid>
        {value?.length > 0 && (
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "center",
              gap: {
                "@200": 2,
                "@": 1,
              },
            }}
          >
            {value?.map((file) => (
              <Grid key={file.id}>
                <PreviewItem {...file} multiple={multiple} />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>
    </StyledUploadDropZone>
  );
};

interface DragZoneImageUploadContentProps {
  multiple?: boolean;
  helperText?: string;
  uploadButtonComponent: UploadInputProps["slots"]["upload"];
}

const DragZoneImageUploadContent: React.FC<DragZoneImageUploadContentProps> = ({
  multiple,
  uploadButtonComponent: UploadButtonComponent,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const secondaryText = multiple ? t("ImageUpload.multipleHelperText") : undefined;
  return (
    <Stack alignItems="center">
      <MdsIcon className={classes.uploadIcon} htmlColor={theme.palette.grey["400"]} icon={MdsUpload} />
      <Grid className={clsx(classes.textContainer, classes.hideOnDrag)}>
        <Typography variant="body1">
          {multiple ? t("ImageUpload.dropImagesHereOr") : t("ImageUpload.dropImageHereOr")}{" "}
          <UploadButtonComponent>{t("ImageUpload.selectText")}</UploadButtonComponent>
        </Typography>
        {secondaryText && <Typography variant="body2">{secondaryText}</Typography>}
      </Grid>
      <Grid className={clsx(classes.textContainer, classes.showOnDrag)}>
        <Typography>{t("ImageUpload.dropHere")}</Typography>
      </Grid>
    </Stack>
  );
};

const StyledUploadDropZone = styled(UploadDropZone)<UploadDropZoneProps>(({ theme }) => ({
  [`& > .${classes.uploadStack}`]: {
    position: "relative",
    overflow: "hidden",
    [`& > .${classes.textContainer}`]: {
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      height: "2.4rem",
      [`&.${classes.showOnDrag}`]: {
        display: "none",
      },
    },
    border: `1px solid ${theme.palette.grey["400"]}`,
    borderRadius: "6px !important",
    display: "flex",
    alignItems: "center",
    [`& .${classes.uploadButton}`]: {
      border: "none",
      background: "none",
      padding: 0,
      color: theme.palette.primary.light,
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
  },
  [`&.${classes.dragOver}`]: {
    [`& > .${classes.uploadStack}`]: {
      borderStyle: "dashed",
      borderColor: theme.palette.primary.dark,
      [`& .${classes.uploadIcon}`]: {
        color: theme.palette.primary.dark,
      },
      [`& .${classes.textContainer}.${classes.hideOnDrag}`]: {
        display: "none",
      },
      [`& .${classes.textContainer}.${classes.showOnDrag}`]: {
        display: "flex",
      },
    },
  },
  "&:hover": {
    [`& .${classes.uploadIcon}`]: {
      color: theme.palette.primary.dark,
    },
    [`& > .${classes.uploadStack}`]: {
      borderColor: theme.palette.primary.dark,
    },
  },
}));
